/**
 * @fileoverview A seed-packet scan's rows, settled one by one: the list (PacketRow: tick · cut-out · name · ›) and the
 * change dialog each row opens (RowChangeDialog), shared by the "Check and save" packet card (PacketPile), the Seed vault
 * card (PacketKeep) and the review sheet (PacketReview), so a packet is drawn and changed the same way in all (D78 rev 2 §3).
 *
 * The keep mode (B137, the Seed vault card): every row is tickable, a packet Grow could not place too (it is kept as
 * printed), rows start ticked unless the server says the seed is already in her vault ("Already in your Seed vault"),
 * and the dialog has no bed and needs no plant to be done.
 *
 * The rows start from the scan (lib/packetRows) and take their plant and variety once their types' answers are in (the
 * plant picker's varieties answer for each type the scan or a suggestion names, usePickerLookup: W24 Job B). In a row's
 * dialog: what the packet says, Grow's suggestion for one it could not place ("Grow thinks this is bee balm …", one
 * Yes: lib/acceptSuggestion, unchanged), the plant fields, the new variety's name, a bed of its own, Done, and
 * "Remove this packet", which takes it out with a short toast and no Undo (B45). A row given a plant is ticked.
 *
 * Where: PacketPile and PacketKeep (photo-tray), PacketReview.
 * API: the plant picker (GET /plants/picker/crops and one GET /plants/picker/varieties per type); for a suggestion, POST /plants/identify (free) and POST /plants/own (an own
 * plant, on Yes).
 * Words (en/es): packets.changeTitle, packets.removeThis, packets.printed, packets.removed, packets.whichPlant, packets.keep.inVault,
 * tray.kinds.ownBed; unknown.* (UnknownPlantCard's, failed).
 *
 * @module usePacketRows
 */

import React from 'react';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePickerLookup, type PickerLookup } from '@/shared/components/plant-fields/usePlantPicker';
import type { BedName } from '@/shared/api/locations';
import type { PacketScan } from '@/shared/api/packets';
import { PacketCut, PacketThumb } from '../components/PacketPicture';
import { PacketRow } from '../components/PacketRow';
import { RowChangeDialog } from '../components/RowChangeDialog';
import { RowSuggestion } from '../components/RowSuggestion';
import {
  chosenVariety, isPlaced, rowWords, rowsOf, seededRows, type OwnBed, type PacketRowState,
} from './packetRows';

/** What the rows are given. */
export interface PacketRowsOptions {
  /** The packets as read. */
  scan: PacketScan;
  /** Packets (by their place) to start out taken out. */
  removedKeys?: readonly number[];
  /** The bed the others go to, by name, for "Same as the others (Back bed)". */
  sharedBed: string;
  /** Their live beds, for a row's own bed. */
  beds: readonly BedName[];
  /** True while the card or sheet is saving. */
  disabled: boolean;
  /** For the fields' ids. */
  idBase: string;
  /** The Seed vault card's rows (B137): every row tickable, no bed, kept as printed when not placed. */
  keep?: boolean;
}

/** What the hook hands back. */
export interface PacketRows {
  /** The rows' types' answers the rows are named and filed by (usePickerLookup). */
  index: PickerLookup;
  /** Every row not taken out, in the photo's order. */
  shown: PacketRowState[];
  /** The rows that will be filed: ticked and placed (keep mode: ticked). */
  kept: PacketRowState[];
  /** How many rows shown still have no plant (Grow could not place them): said above the button, never dropped silently; 0 in keep mode. */
  unplaced: number;
  /** The list of rows. */
  list: React.ReactNode;
  /** The open row's change dialog: render it once. */
  dialog: React.ReactNode;
}

/**
 * A row's picture: its own packet cut out of the photo, or the whole photo when it has no position. A picture only (B59).
 *
 * @param scan - The packets as read.
 * @param row - The row.
 */
const pictureOf = (scan: PacketScan, row: PacketRowState): React.ReactNode => (
  row.box ? <PacketCut src={scan.cardUrl} box={row.box} /> : <PacketThumb src={scan.thumbUrl} className="shrink-0" />
);

/**
 * A scan's rows, their list and the dialog a row opens.
 *
 * @param options - PacketRowsOptions.
 * @returns PacketRows.
 */
export function usePacketRows({ scan, removedKeys = [], sharedBed, beds, disabled, idBase, keep = false }: PacketRowsOptions): PacketRows {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const [rows, setRows] = React.useState<PacketRowState[]>(() => rowsOf(scan, removedKeys, keep));
  // The types the scan placed its packets under or suggests, and the ones the rows are on now: their names and varieties.
  const index = usePickerLookup([
    ...scan.packets.flatMap((packet) => [packet.plantId, packet.suggestion?.plantId]),
    ...rows.map((row) => row.choice.plantId),
  ]);
  // Each row's plant and variety set once its type's answer is in (it is cached, so usually at once).
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current || index.loading) return;
    seeded.current = true;
    setRows((current) => seededRows(current, scan, index));
  }, [index, scan]);
  /** The row whose dialog is open, by key, or null. */
  const [opened, setOpened] = React.useState<number | null>(null);

  /**
   * Changes one row.
   *
   * @param key - The row's key.
   * @param patch - The fields to change.
   */
  const setRow = (key: number, patch: Partial<PacketRowState>): void => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const shown = rows.filter((row) => !row.removed);
  const kept = shown.filter((row) => row.ticked && (keep || isPlaced(row)));
  const unplaced = keep ? 0 : shown.filter((row) => !isPlaced(row)).length;
  const open = opened === null ? null : shown.find((row) => row.key === opened) ?? null;

  /**
   * The bed a row goes to, by name, when it has one of its own.
   *
   * @param bed - Its own bed.
   */
  const ownBedName = (bed: OwnBed): string => (bed.kind === 'new' ? bed.name.trim() || '…' : beds.find((each) => each.id === bed.id)?.name ?? '…');

  const list = (
    <ul className="flex flex-col gap-1.5">
      {shown.map((row) => {
        const words = rowWords(index, row);
        const placed = isPlaced(row);
        // The Seed vault card keeps a packet as printed, so every row is whole and tickable; a duplicate says so.
        const whole = keep || placed;
        return (
          <PacketRow
            key={row.key}
            id={`${idBase}-packet-${row.key}`}
            picture={pictureOf(scan, row)}
            name={whole ? words.name : row.kind}
            detail={keep ? (row.inVault ? t('packets.keep.inVault') : null) : placed ? null : t('packets.whichPlant')}
            bedLine={row.bed ? t('tray.kinds.ownBed', { bed: ownBedName(row.bed) }) : null}
            ticked={row.ticked}
            tickable={whole}
            onTick={(on) => setRow(row.key, { ticked: on })}
            onOpen={() => setOpened(row.key)}
            disabled={disabled}
          />
        );
      })}
    </ul>
  );

  const words = open ? rowWords(index, open) : null;
  const dialog = open && words ? (
    <RowChangeDialog
      open
      onClose={() => setOpened(null)}
      title={t('packets.changeTitle')}
      picture={pictureOf(scan, open)}
      name={keep || isPlaced(open) ? words.name : open.kind}
      caption={words.printed ? t('packets.printed', { text: words.printed }) : null}
      suggestion={!isPlaced(open) && scan.packets[open.key] ? (
        <RowSuggestion
          packet={scan.packets[open.key]}
          onAccept={(placed) => {
            const plantId = placed.plantId as number;
            // An own plant made just now is in the same crop as the plant it went under.
            const cropId = index.cropOf(plantId) ?? index.cropOf(scan.packets[open.key].suggestion?.plantId ?? plantId);
            const varietyId = placed.varietyId ?? (placed.newVariety ? null : index.varietiesOf(plantId).start);
            setRow(open.key, {
              choice: { cropId, plantId, varietyId },
              newVariety: placed.newVariety,
              known: placed.varietyId != null && placed.varietyName ? { id: placed.varietyId, name: placed.varietyName } : null,
              joinKind: placed.joinKind ?? null,
              edited: true,
              ticked: true,
            });
            setOpened(null);
          }}
        />
      ) : null}
      value={open.choice}
      // A row given its plant is ticked: it is what the gardener is answering.
      onChange={(choice) => setRow(open.key, { choice, edited: true, ...(isPlaced({ choice }) && !isPlaced(open) ? { ticked: true } : {}) })}
      newVariety={open.newVariety !== null && !chosenVariety(index, open)
        ? { value: open.newVariety, onChange: (name) => setRow(open.key, { newVariety: name, edited: true }) }
        : null}
      sharedBed={sharedBed}
      bed={open.bed}
      onBed={(bed) => setRow(open.key, { bed })}
      beds={beds}
      removeLabel={t('packets.removeThis')}
      onRemove={() => {
        setRow(open.key, { removed: true });
        setOpened(null);
        toast({ title: t('packets.removed', { name: words.printed || open.kind }) });
      }}
      doneDisabled={!keep && !isPlaced(open)}
      showBed={!keep}
      idPrefix={`${idBase}-change-${open.key}`}
    />
  ) : null;

  return { index, shown, kept, unplaced, list, dialog };
}
